import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiArrowLeft, FiPlus, FiCheck } from "react-icons/fi";

// Reusable Components
import { PageHeader } from "../../components/common";

// Page-specific Components
import RecipientSelectionBar from "../../components/recipients/RecipientSelectionBar";
import RecipientTabs from "../../components/recipients/RecipientTabs";
import RecipientsTable from "../../components/recipients/RecipientsTable";
import AddContactModal from "../../components/recipients/AddContactModal";
import CSVPreviewModal from "../../components/recipients/CSVPreviewModal";

// Services
import { ContactsService, UserContact } from "../../services/contactsService";
import { apiService } from "../../services/apiService";

// Styles
import "../../styles/recipients.css";

// Type-safe icon wrappers
const ArrowLeftIcon = FiArrowLeft as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;

// Default contact types
const DEFAULT_TYPES = [
  "INVESTOR",
  "ADVISOR",
  "CUSTOMER",
  "TEAM",
  "PARTNER",
  "OTHER",
];

// Extended contact interface with type
export interface RecipientContact extends UserContact {
  type?: string;
}

// Parsed CSV contact
export interface ParsedCSVContact {
  name: string;
  email: string;
  type: string;
  isValid: boolean;
  error?: string;
}

/**
 * ChooseRecipientsPage - Recipients selection screen in the update flow
 */
const ChooseRecipientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Contact state
  const [contacts, setContacts] = useState<RecipientContact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // VC contacts state
  const [myVCs, setMyVCs] = useState<RecipientContact[]>([]);
  const [isLoadingVCs, setIsLoadingVCs] = useState(false);

  // Selection state - persisted via URL params
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    const selected = searchParams.get("selected");
    return selected ? new Set(selected.split(",").filter(Boolean)) : new Set();
  });

  // Get subject from URL (passed from compose page)
  const emailSubject = searchParams.get("subject") || "";

  // Tab state
  const [activeTab, setActiveTab] = useState<string>("all");

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCSVPreview, setShowCSVPreview] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState<ParsedCSVContact[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  // Extract unique contact types from contacts
  const contactTypes = useMemo(() => {
    const types = new Map<string, number>();
    contacts.forEach((contact) => {
      const type = contact.type || contact.settings?.provider?.toUpperCase() || "OTHER";
      types.set(type, (types.get(type) || 0) + 1);
    });
    // Add VC type if there are VCs
    if (myVCs.length > 0) {
      types.set("VC", myVCs.length);
    }
    // Add default types that don't exist yet with 0 count
    DEFAULT_TYPES.forEach((type) => {
      if (!types.has(type)) {
        types.set(type, 0);
      }
    });
    return Array.from(types.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => {
        // Sort by default types order first, then alphabetically
        const aIndex = DEFAULT_TYPES.indexOf(a.type);
        const bIndex = DEFAULT_TYPES.indexOf(b.type);
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.type.localeCompare(b.type);
      });
  }, [contacts, myVCs]);

  // Filtered contacts based on active tab
  const filteredContacts = useMemo(() => {
    if (activeTab === "all") {
      return [...contacts, ...myVCs];
    }
    if (activeTab === "VC") {
      return myVCs;
    }
    return contacts.filter((contact) => {
      const type = contact.type || contact.settings?.provider?.toUpperCase() || "OTHER";
      return type === activeTab;
    });
  }, [contacts, activeTab, myVCs]);

  // Load contacts on mount
  useEffect(() => {
    loadContacts();
    loadMyVCs();
  }, []);

  // Persist selected IDs to URL
  useEffect(() => {
    const selectedArray = Array.from(selectedIds);
    if (selectedArray.length > 0) {
      setSearchParams({ selected: selectedArray.join(",") }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [selectedIds, setSearchParams]);

  const loadContacts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const userContacts = await ContactsService.getUserContacts();
      // Map contacts to include type from settings
      const mappedContacts: RecipientContact[] = userContacts.map((c) => ({
        ...c,
        type: c.settings?.provider?.toUpperCase() || "OTHER",
      }));
      setContacts(mappedContacts);
    } catch (err: any) {
      console.error("Failed to load contacts:", err);
      setError(err?.message || "Failed to load contacts");
    } finally {
      setIsLoading(false);
    }
  };

  const loadMyVCs = async () => {
    setIsLoadingVCs(true);
    try {
      const response = await apiService.getMyVCs();
      if (response.success && response.data) {
        console.log('My VCs response:', response.data);
        // Transform VC data to contact format
        const vcContacts: RecipientContact[] = response.data.map((vc: any) => ({
          id: vc.vc_id,
          user_id: vc.vc_id,
          email: vc.email || '',
          name: vc.full_name || vc.email || 'VC',
          type: "VC",
          created_at: new Date().toISOString(),
        }));
        console.log('Transformed VC contacts:', vcContacts);
        setMyVCs(vcContacts);
      }
    } catch (error) {
      console.error('Failed to load VCs:', error);
      // Don't show error - VCs are optional
    } finally {
      setIsLoadingVCs(false);
    }
  };

  // Selection handlers
  const handleToggleContact = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    // Toggle all VISIBLE contacts (respecting current filter)
    const visibleIds = filteredContacts.map((c) => c.id);
    const allSelected = visibleIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        // Deselect all visible
        visibleIds.forEach((id) => next.delete(id));
      } else {
        // Select all visible
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [filteredContacts, selectedIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Navigation handlers
  const handleBackToCompose = () => {
    // Navigate back to compose page with selected recipients preserved
    const selectedArray = Array.from(selectedIds);
    if (selectedArray.length > 0) {
      navigate(`/updates/new?recipients=${selectedArray.join(",")}`);
    } else {
      navigate("/updates/new");
    }
  };

  const handleNextSchedule = () => {
    // Navigate to schedule page with selected recipients and subject
    const selectedArray = Array.from(selectedIds);
    const params = new URLSearchParams();
    if (selectedArray.length > 0) {
      params.set("selected", selectedArray.join(","));
    }
    if (emailSubject) {
      params.set("subject", emailSubject);
    }
    const queryString = params.toString();
    navigate(`/updates/schedule${queryString ? `?${queryString}` : ""}`);
  };

  // Add contact handlers
  const handleAddManualContact = async (contact: {
    name: string;
    email: string;
    type: string;
  }) => {
    try {
      const newContact = await ContactsService.createContact({
        email: contact.email,
        name: contact.name,
        settings: { provider: contact.type.toLowerCase() },
      });

      // Add to contacts list with type
      const mappedContact: RecipientContact = {
        ...newContact,
        type: contact.type,
      };
      setContacts((prev) => [mappedContact, ...prev]);

      // Auto-select the new contact
      setSelectedIds((prev) => new Set([...Array.from(prev), newContact.id]));

      setShowAddModal(false);
    } catch (err: any) {
      console.error("Failed to create contact:", err);
      alert(err?.message || "Failed to create contact");
    }
  };

  const handleGoogleImport = async () => {
    try {
      // Use existing OAuth contacts endpoint
      const response = await ContactsService.getOAuthContacts();
      if (response.contacts && response.contacts.length > 0) {
        // Save selected contacts
        await ContactsService.saveSelectedContacts(response.contacts);
        // Reload contacts
        await loadContacts();
        setShowAddModal(false);
      } else {
        alert("No contacts found in Google account");
      }
    } catch (err: any) {
      console.error("Failed to import Google contacts:", err);
      alert(err?.message || "Failed to import Google contacts");
    }
  };

  const handleCSVUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCSV(text);
      setCsvPreviewData(parsed);
      setShowCSVPreview(true);
      setShowAddModal(false);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string): ParsedCSVContact[] => {
    const lines = text.split("\n").filter((line) => line.trim());
    if (lines.length === 0) return [];

    // Parse header
    const header = lines[0].toLowerCase().split(",").map((h) => h.trim());
    const nameIndex = header.findIndex((h) =>
      ["name", "full name", "contact name"].includes(h)
    );
    const emailIndex = header.findIndex((h) =>
      ["email", "email address", "e-mail"].includes(h)
    );
    const typeIndex = header.findIndex((h) =>
      ["type", "category", "group", "tag"].includes(h)
    );

    // Validate header
    if (emailIndex === -1) {
      return [
        {
          name: "",
          email: "",
          type: "",
          isValid: false,
          error: "CSV must have an 'email' column",
        },
      ];
    }

    // Parse rows
    const contacts: ParsedCSVContact[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
      const email = values[emailIndex] || "";
      const name = nameIndex !== -1 ? values[nameIndex] || "" : "";
      const type = typeIndex !== -1 ? values[typeIndex]?.toUpperCase() || "OTHER" : "OTHER";

      // Validate
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      const isValid = isValidEmail && name.trim() !== "";

      contacts.push({
        name,
        email,
        type: DEFAULT_TYPES.includes(type) ? type : "OTHER",
        isValid,
        error: !isValid
          ? !name.trim()
            ? "Name is required"
            : !isValidEmail
            ? "Invalid email format"
            : undefined
          : undefined,
      });
    }

    return contacts;
  };

  const handleConfirmCSVImport = async () => {
    setIsImporting(true);
    try {
      const validContacts = csvPreviewData.filter((c) => c.isValid);
      
      // Create contacts one by one
      const createdIds: string[] = [];
      for (const contact of validContacts) {
        try {
          const newContact = await ContactsService.createContact({
            email: contact.email,
            name: contact.name,
            settings: { provider: contact.type.toLowerCase() },
          });
          createdIds.push(newContact.id);
        } catch (err) {
          console.error(`Failed to create contact ${contact.email}:`, err);
        }
      }

      // Reload contacts and select newly created ones
      await loadContacts();
      setSelectedIds((prev) => new Set([...Array.from(prev), ...createdIds]));

      setShowCSVPreview(false);
      setCsvPreviewData([]);
    } catch (err: any) {
      console.error("Failed to import CSV contacts:", err);
      alert(err?.message || "Failed to import contacts");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="su-recipients-page">
      {/* Page Header */}
      <PageHeader
        title="Choose Recipients"
        subtitle="Select contacts for your investor update"
      />

      {/* Selection Bar */}
      <RecipientSelectionBar
        selectedCount={selectedIds.size}
        onClearSelection={handleClearSelection}
      />

      {/* Tabs */}
      <RecipientTabs
        activeTab={activeTab}
        contactTypes={contactTypes}
        totalCount={contacts.length}
        onTabChange={setActiveTab}
        onAddContactClick={() => setShowAddModal(true)}
      />

      {/* Table Section */}
      <div className="su-section su-recipients-table-container">
        {isLoading ? (
          <div className="su-recipients-loading">
            <div className="su-recipients-spinner" />
            <span>Loading contacts...</span>
          </div>
        ) : error ? (
          <div className="su-recipients-error">
            <span>{error}</span>
            <button onClick={loadContacts} className="su-btn-secondary">
              Retry
            </button>
          </div>
        ) : (
          <RecipientsTable
            contacts={filteredContacts}
            selectedIds={selectedIds}
            onToggleContact={handleToggleContact}
            onToggleAll={handleToggleAll}
          />
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="su-recipients-bottom-nav">
        <button
          className="su-btn-secondary su-recipients-back-btn"
          onClick={handleBackToCompose}
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>BACK TO COMPOSE</span>
        </button>

        <button
          className={`su-btn-primary su-recipients-next-btn ${selectedIds.size === 0 ? "su-btn-disabled" : ""}`}
          disabled={selectedIds.size === 0}
          onClick={handleNextSchedule}
          title={selectedIds.size === 0 ? "Select at least one recipient" : "Continue to scheduling"}
        >
          <span>NEXT: SCHEDULE</span>
          <CheckIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddContactModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onCSVUpload={handleCSVUpload}
          onGoogleImport={handleGoogleImport}
          onAddManual={handleAddManualContact}
          existingTypes={DEFAULT_TYPES}
        />
      )}

      {showCSVPreview && (
        <CSVPreviewModal
          isOpen={showCSVPreview}
          data={csvPreviewData}
          onClose={() => {
            setShowCSVPreview(false);
            setCsvPreviewData([]);
          }}
          onConfirmImport={handleConfirmCSVImport}
          isImporting={isImporting}
        />
      )}
    </div>
  );
};

export default ChooseRecipientsPage;

